import React, { useState, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import {
  TextField,
  Button,
  Paper,
  Typography,
  Box,
  MenuItem,
  Alert,
  InputLabel,
  FormControl,
  Select,
} from "@mui/material";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Autocomplete } from "@mui/material";
import debounce from "lodash/debounce";
import authFetch from "../utils/authFetch";
import { toast } from "react-toastify";

const UserComponent = () => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      name: "",
      birthDate: null,
      birthTime: null,
      birthPlace: "",
      birthPlaceId: "",
      gender: "",
      timezone: "",
    },
  });

  const [options, setOptions] = useState([]);
  const birthPlaceValue = watch("birthPlace");
  const birthPlaceIdValue = watch("birthPlaceId");

  const isSelectionRef = useRef(false);

  const [timezones, setTimezones] = useState([
    { name: "Pacific/Midway", label: "Pacific/Midway (-11:00)" },
    { name: "America/Adak", label: "America/Adak (-10:00)" },
    { name: "America/Anchorage", label: "America/Anchorage (-09:00)" },
    { name: "America/Los_Angeles", label: "America/Los_Angeles (-08:00)" },
    { name: "America/Denver", label: "America/Denver (-07:00)" },
    { name: "America/Chicago", label: "America/Chicago (-06:00)" },
    { name: "America/New_York", label: "America/New_York (-05:00)" },
    { name: "America/Caracas", label: "America/Caracas (-04:30)" },
    { name: "America/Halifax", label: "America/Halifax (-04:00)" },
    { name: "America/St_Johns", label: "America/St_Johns (-03:30)" },
    {
      name: "America/Argentina/Buenos_Aires",
      label: "America/Argentina/Buenos_Aires (-03:00)",
    },
    {
      name: "Atlantic/South_Georgia",
      label: "Atlantic/South_Georgia (-02:00)",
    },
    { name: "Atlantic/Azores", label: "Atlantic/Azores (-01:00)" },
    { name: "Europe/London", label: "Europe/London (+00:00)" },
    { name: "Europe/Paris", label: "Europe/Paris (+01:00)" },
    { name: "Europe/Athens", label: "Europe/Athens (+02:00)" },
    { name: "Asia/Jerusalem", label: "Asia/Jerusalem (+02:00)" },
    { name: "Asia/Baghdad", label: "Asia/Baghdad (+03:00)" },
    { name: "Asia/Dubai", label: "Asia/Dubai (+04:00)" },
    { name: "Asia/Karachi", label: "Asia/Karachi (+05:00)" },
    { name: "Asia/Kolkata", label: "Asia/Kolkata (+05:30)" },
    { name: "Asia/Kathmandu", label: "Asia/Kathmandu (+05:45)" },
    { name: "Asia/Dhaka", label: "Asia/Dhaka (+06:00)" },
    { name: "Asia/Bangkok", label: "Asia/Bangkok (+07:00)" },
    { name: "Asia/Shanghai", label: "Asia/Shanghai (+08:00)" },
    { name: "Asia/Tokyo", label: "Asia/Tokyo (+09:00)" },
    { name: "Australia/Adelaide", label: "Australia/Adelaide (+09:30)" },
    { name: "Australia/Sydney", label: "Australia/Sydney (+10:00)" },
    { name: "Pacific/Guam", label: "Pacific/Guam (+10:00)" },
    { name: "Pacific/Fiji", label: "Pacific/Fiji (+12:00)" },
    { name: "Pacific/Chatham", label: "Pacific/Chatham (+12:45)" },
    { name: "Pacific/Tongatapu", label: "Pacific/Tongatapu (+13:00)" },
    { name: "Pacific/Kiritimati", label: "Pacific/Kiritimati (+14:00)" },
  ]);

  const selectedTimezone = watch("timezone");

  // Watch all form fields to determine if form is valid
  const watchedFields = watch();
  const isFormValid =
    watchedFields.name &&
    watchedFields.birthDate &&
    watchedFields.birthTime &&
    watchedFields.birthPlace &&
    watchedFields.birthPlaceId &&
    watchedFields.gender;

  const fetchSuggestions = useMemo(
    () =>
      debounce(async (query) => {
        if (!query) {
          setOptions([]);
          return;
        }
        try {
          const res = await authFetch.get(
            `/user/public/fetch-location-suggestions?search=${encodeURIComponent(
              query
            )}`
          );
          const suggestions = res?.data?.data || [];
          setOptions(suggestions);
        } catch (err) {
          console.error("Failed to fetch suggestions", err);
        }
      }, 500),
    []
  );

  useEffect(() => {
    if (isSelectionRef.current) return;
    fetchSuggestions(birthPlaceValue);
  }, [birthPlaceValue, fetchSuggestions]);

  const onSubmit = async (data) => {
    try {
      const birthDateTime = (() => {
        if (!data.birthDate || !data.birthTime) return null;

        const date = new Date(data.birthDate);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        const time = new Date(data.birthTime);
        const hours = String(time.getHours()).padStart(2, "0");
        const minutes = String(time.getMinutes()).padStart(2, "0");
        const seconds = "00";

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      })();

      const genderMap = {
        Male: "MALE",
        Female: "FEMALE",
        "Prefer not to say": "OTHER",
      };

      const submissionData = {
        name: data.name,
        gender: genderMap[data.gender] || "OTHER",
        birthDetails: {
          date: birthDateTime,
          birthLocationPlaceId: data.birthPlaceId,
        },
        userCurrentTimezone: selectedTimezone || null,
      };

      // Get browser timezone
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await authFetch.post(
        "/user/admin/sandbox-customer",
        submissionData,
        {
          headers: {
            "x-user-timezone": browserTimezone,
          },
        }
      );

      toast.success("User created successfully!");
      reset();
      setOptions([]);
    } catch (err) {
      // Extract error message from API response
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "An error occurred while creating the user";
      toast.error(errorMessage);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>
        Create User
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          fullWidth
          label="Full Name"
          {...register("name", { required: "Name is required" })}
          error={!!errors.name}
          helperText={errors.name?.message}
        />

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Date of Birth"
            value={watch("birthDate")}
            onChange={(date) => setValue("birthDate", date)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.birthDate,
                helperText: errors.birthDate?.message,
              },
            }}
          />

          <TimePicker
            label="Time of Birth"
            value={watch("birthTime")}
            onChange={(time) => setValue("birthTime", time)}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.birthTime,
                helperText: errors.birthTime?.message,
              },
            }}
          />
        </LocalizationProvider>

        <FormControl fullWidth>
          <InputLabel
            id="timezone-label"
            sx={{
              backgroundColor: "white",
              px: 1,
            }}
          >
            User Current Timezone
          </InputLabel>
          <Select
            labelId="timezone-label"
            id="timezone"
            label="Timezone"
            {...register("timezone")}
            onChange={(e) => setValue("timezone", e.target.value)}
            value={selectedTimezone || ""}
          >
            <MenuItem value="">
              <em>Select a timezone</em>
            </MenuItem>
            {timezones.map((tz) => (
              <MenuItem key={tz.name} value={tz.name}>
                {tz.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          freeSolo
          options={options}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name || option
          }
          value={null}
          inputValue={birthPlaceValue || ""}
          onInputChange={(event, newValue) => {
            if (event?.type === "change") {
              isSelectionRef.current = false;
            }

            setValue("birthPlace", newValue || "");
            // Clear placeId when typing (not when selecting from dropdown)

            // if (event && event.type === "change") {
            //   setValue("birthPlaceId", "");
            // }

            if (!isSelectionRef.current) {
              setValue("birthPlaceId", "");
            }
          }}
          onChange={(event, newValue) => {
            if (
              newValue &&
              typeof newValue === "object" &&
              newValue.name &&
              newValue.placeId
            ) {
              // Selected from dropdown - we know it has name and placeId
              isSelectionRef.current = true;

              setValue("birthPlace", newValue.name);
              setValue("birthPlaceId", newValue.placeId);
            } else if (typeof newValue === "string") {
              // Typed value - try to find matching option
              setValue("birthPlace", newValue);
              const matchingOption = options.find((o) => o.name === newValue);
              if (matchingOption && matchingOption.placeId) {
                setValue("birthPlaceId", matchingOption.placeId);
              } else {
                setValue("birthPlaceId", "");
              }
            } else {
              setValue("birthPlace", "");
              setValue("birthPlaceId", "");
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Birth Place"
              {...register("birthPlace", {
                required: "Birth Place is required",
              })}
            />
          )}
        />

        <TextField
          fullWidth
          select
          label="Gender"
          defaultValue=""
          {...register("gender", { required: "Gender is required" })}
          error={!!errors.gender}
          helperText={errors.gender?.message}
        >
          <MenuItem value="">Select gender</MenuItem>
          <MenuItem value="Male">Male</MenuItem>
          <MenuItem value="Female">Female</MenuItem>
          <MenuItem value="Prefer not to say">Prefer not to say</MenuItem>
        </TextField>

        <Button
          type="submit"
          variant="contained"
          color="primary"
          size="large"
          disabled={isSubmitting || !isFormValid}
          fullWidth
        >
          {isSubmitting ? "Creating User..." : "Create User"}
        </Button>
      </Box>
    </Paper>
  );
};

export default UserComponent;
